import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Public as GeographicIcon,
  DonutSmall as CompetitiveIcon,
  TrendingUp as ProjectionsIcon,
  Image as ImageIcon,
  Download as DownloadIcon
} from '@mui/icons-material';

const ExportModal = ({ 
  open, 
  onClose, 
  onExport, 
  availableSections = { geographic: true, competitive: true, projections: true },
  exporting = false
}) => {
  const [selectedSections, setSelectedSections] = useState({
    geographic: true,
    competitive: true,
    projections: true
  });
  const [includeVisuals, setIncludeVisuals] = useState(true);

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExport = () => {
    const hasSelection = Object.values(selectedSections).some(v => v);
    if (!hasSelection) {
      return; // Prevent export if nothing selected
    }
    onExport(selectedSections, includeVisuals);
  };

  const handleClose = () => {
    if (!exporting) {
      onClose();
    }
  };

  const hasSelection = Object.values(selectedSections).some(v => v);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DownloadIcon />
          <Typography variant="h6">Export Options</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose which sections to include in your exported HTML report.
        </Typography>

        {/* Section Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Sections to Include
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedSections.geographic}
                  onChange={() => handleSectionToggle('geographic')}
                  disabled={!availableSections.geographic || exporting}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GeographicIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography>Geographic Summary</Typography>
                  {!availableSections.geographic && (
                    <Typography variant="caption" color="text.secondary">(Not generated)</Typography>
                  )}
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedSections.competitive}
                  onChange={() => handleSectionToggle('competitive')}
                  disabled={!availableSections.competitive || exporting}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompetitiveIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography>Competitive Summary</Typography>
                  {!availableSections.competitive && (
                    <Typography variant="caption" color="text.secondary">(Not generated)</Typography>
                  )}
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedSections.projections}
                  onChange={() => handleSectionToggle('projections')}
                  disabled={!availableSections.projections || exporting}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ProjectionsIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography>Projections Summary</Typography>
                  {!availableSections.projections && (
                    <Typography variant="caption" color="text.secondary">(Not generated)</Typography>
                  )}
                </Box>
              }
            />
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Visual Options */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Visual Options
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeVisuals}
                onChange={(e) => setIncludeVisuals(e.target.checked)}
                disabled={exporting}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                <Typography>Include Charts & Visualizations</Typography>
              </Box>
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5, display: 'block' }}>
            Embeds charts as images in the HTML file (may take a few seconds)
          </Typography>
        </Box>

        {!hasSelection && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Please select at least one section to export
          </Alert>
        )}

        {exporting && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                {includeVisuals 
                  ? 'Capturing visualizations and preparing export...' 
                  : 'Preparing export...'}
              </Typography>
            </Box>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={exporting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={!hasSelection || exporting}
          startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
        >
          {exporting ? 'Exporting...' : 'Export HTML'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal;



